import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';

import { StateLanguage } from '../languages/config/StateLanguage';
import { ApiDigitalAuthAuthentication } from '../services/auth/authentication.service';
import { UserToken } from '../models/user.model';
import { AppServiceToken } from '../models/app.service.model';
import { CompanyToken } from '../models/company.model';
import { Container } from '../styles/container.style';
import { Footer } from '../styles/footer.style';
import { Error } from '../models/error.model';
import { TokenSwitch } from '../models/token.model';
import { GlobalSwal } from '../styles/swal.style';
import { headerShow, headerHide } from '../utils/header.util';
import { navBarShow, navBarHide } from '../utils/nav.bar.util';
import { dropdownShow, dropdownHide } from '../utils/dropdown.util';
import { navTabReload } from '../utils/nav.tab.util';
import { removeTooltip, uploadTooltip } from '../utils/tooltip.util';
import { offcanvasAsideHide } from '../utils/offcanvas.util';
import { imageUser } from '../scripts/image.value.script';
import { imageCompany } from '../scripts/image.value.script';
import { countryName } from '../libraries/country.library';
import { getError } from '../languages/translations/response';
import { formatLocation } from '../scripts/format.value.script';
import { errorWebHandler } from '../scripts/error.web.handler.script';
import { authMultiCompany, authApiKey, authRefresh, authSwitchCompany, authUser, authService, authCompany, authCompanies } from '../scripts/authentication.script';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useNavAccess } from '../hooks/useNavAccess';
import { useUpgradeModal } from '../contexts/UpgradeModalContext';
import { isChatAllowed } from '../scripts/chat.auth';
import { openChat } from '../scripts/chat.controller';
import { useChatOptional } from '../contexts/ChatContext';

import logo from '../assets/themes/svg/logo-official-light.svg';
import imgDashboard from '../assets/themes/images/display.svg'
import imgContacts from '../assets/themes/images/contacts.svg';
import imgTreasury from '../assets/themes/images/money.svg';
import imgPos from '../assets/themes/images/exhibit.svg';
import imgSales from '../assets/themes/images/selling.svg';
import imgPurchases from '../assets/themes/images/shopping.svg';
import imgInventory from '../assets/themes/images/store.svg';
import imgAccounting from '../assets/themes/images/analysis.svg';
import imgDocuments from '../assets/themes/images/documents.svg';
import imgPayroll from '../assets/themes/images/payroll.svg';
import imgAdministration from '../assets/themes/images/administration.svg';

export interface AppNavbarProps {
  asideMinimize: string,
  activeDrawer: {value: string, aside: string, button: string},
  scrollVisibility: {header: string, top: string},
  setAsideMinimize: Dispatch<SetStateAction<string>>,
  setActiveDrawer: Dispatch<SetStateAction<{value: string, aside: string, button: string}>>,
  setScrollVisibility: Dispatch<SetStateAction<{header: string, top: string}>>,
  notificationsUnread: number,
  loadSession: Function,
  companiesForUser?: CompanyToken[] | null
};

let errorResponse: Error, tokenSwitchResponse: TokenSwitch;

const AppNavbar: React.FunctionComponent<AppNavbarProps> = ({asideMinimize, activeDrawer, scrollVisibility, setAsideMinimize, setActiveDrawer, setScrollVisibility, notificationsUnread, loadSession, companiesForUser}) => {
  const {lang} = StateLanguage()
  const location = useLocation()
  const param = useParams()
  const navigate = useNavigate()
  const didRun = useRef(false)
  const { can: canSub } = useSubscription()
  const showSubscriptionMenu = canSub('read', 'digital:auth:entity:subscription:*').allowed
  const payrollAccess = useNavAccess('read', 'digital:payroll:*', 'payroll')
  const { openUpgradeModal } = useUpgradeModal()
  const chatAllowed = isChatAllowed()
  const chatCtx = useChatOptional()
  const chatUnread = chatCtx?.unreadTotal ?? 0

  const [footerNavbar, setFooterNavbar] = useState(false)
  const [searchAnimation, setSearchAnimation] = useState({spinner: false, clear: false})
  const [searchData, setSearchData] = useState({text: '', list_app_services: [] as AppServiceToken[], list_companies: [] as CompanyToken[]})
  const [user, setUser] = useState<UserToken | null>(null)
  const [appService, setAppService] = useState<AppServiceToken | null>(null)
  const [company, setCompany] = useState<CompanyToken | null>(null)
  const [companies, setCompanies] = useState<CompanyToken[] | null>(null)
  const [multiCompany, setMultiCompany] = useState<boolean>(false)
  const [apiKey, setApiKey] = useState<string | null>(null)
  const [tokenRefresh, setTokenRefresh] = useState<string | null>(null)

  const loadUserLoggedIn = async () => {
    setUser(authUser())
    setAppService(authService())
    setCompany(authCompany())
    setCompanies(authCompanies())
    setMultiCompany(authMultiCompany())
    setApiKey(authApiKey())
    setTokenRefresh(authRefresh())
  }

  const submitCompanySelected = async (item: CompanyToken) => {
    if (apiKey && tokenRefresh && company && company.id !== item.id) {
      let shouldSwitch: boolean = /\/(([a-z]{2})\/)?main\/dashboard/.test(location.pathname) || /\/(([a-z]{2})\/)?main\/company\/(view|update)/.test(location.pathname) || /\/list$/.test(location.pathname)

      if (shouldSwitch) {
        await ApiDigitalAuthAuthentication.switchCompany(apiKey, tokenRefresh, item.id).then( (response) => {
          if (response.status === 200) {
            tokenSwitchResponse = response.data

            authSwitchCompany(tokenSwitchResponse)
            setCompany(authCompany())
            setCompanies(authCompanies())
            setApiKey(authApiKey())
            setTokenRefresh(authRefresh())
            loadSession()
          } else {
            errorResponse = response.data

            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              confirmButtonText: lang.labels.okGotIt
            })
          }
        }).catch( (error) => {
          errorWebHandler(lang, error)
        })
      } else {
        GlobalSwal.fire({
          title: lang.labels.itNotChangeCompany,
          text: lang.labels.companySwitchingNotAvailableInView,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }

    executeNavbarAsideHide()
  }

  const submitCompanySelectedInSearch = (item: CompanyToken) => {
    setSearchData({text: '', list_app_services: [], list_companies: []})
    setSearchAnimation({spinner: false, clear: false})
    submitCompanySelected(item)
    executeOverlayTransparent()
  }

  const handleChangeSearch = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let text: string = event.target.value
    let listAppServices: AppServiceToken[] = []
    let listCompanies: CompanyToken[] = []

    if (text.length > 0) {
      setSearchAnimation({spinner: true, clear: false})

      if (appService) {
        listAppServices.push(appService)
      }
      if (companies) {
        for (let company of companies) {
          let name: string = company.name.toLowerCase()
          if (name.indexOf(text.toLowerCase()) !== -1) {
            listCompanies.push(company)
          }
        }
      }

      setTimeout( () => {
        setSearchAnimation({spinner: false, clear: true})
      }, 200 )

      dropdownShow('dropdown-search', 'container-search', 'overlay-menu-transparent', true)
    } else {
      setSearchAnimation({spinner: false, clear: false})
      dropdownHide('dropdown-search', 'container-search', 'overlay-menu-transparent', true)
    }

    setSearchData({text: text, list_app_services: listAppServices, list_companies: listCompanies})
  }

  const executeClearSearch = () => {
    setSearchAnimation({spinner: false, clear: false})
    setSearchData({text: '', list_app_services: [], list_companies: []})
    executeOverlayTransparent()
  }

  const executeClickOnTab = (item: string) => {
    let locationNavTab: string = (param.language) ? location.pathname.split('/')[3] : location.pathname.split('/')[2]

    viewFooterNavbar(item)

    if (user && multiCompany && window.innerWidth > 992) {
      switch (item) {
        case 'nav-tab-companies':
          if (asideMinimize === 'on') {
            setAsideMinimize('off')
            navBarHide('button-navbar-aside-minimize')
          }
          break;
        case 'nav-tab-dashboard':
          if (locationNavTab !== 'dashboard' && locationNavTab !== 'company') {
            navigate('/main/dashboard')
          }
          break;
        case 'nav-tab-contacts':
          if (locationNavTab !== 'contact') {
            navigate('/main/contact/list')
          }
          break;
        case 'nav-tab-treasury':
          if (locationNavTab !== 'treasury') {
            navigate('/main/treasury/list')
          }
          break;
        case 'nav-tab-pos':
          if (locationNavTab !== 'pos') {
            navigate('/main/pos/list')
          }
          break;
        case 'nav-tab-sales':  
          if (locationNavTab !== 'sale') {
            navigate('/main/sale/quote/list')
          }
          break;
        case 'nav-tab-purchases':  
          if (locationNavTab !== 'purchase') {
            navigate('/main/purchase/order/list')
          }
          break;
        case 'nav-tab-inventory':  
          if (locationNavTab !== 'inventory') {
            navigate('/main/inventory/category/list')
          }
          break;
        case 'nav-tab-accounting':
          if (locationNavTab !== 'accounting') {
            navigate('/main/accounting/journal/list')
          }
          break;
        case 'nav-tab-payroll':
          if (locationNavTab !== 'payroll') {
            navigate('/main/payroll/employees/list')
          }
          break;
        case 'nav-tab-admin':
          if (locationNavTab !== 'admin') {
            navigate('/main/admin/users')
          }
          break;
      }
    }
  }

  const executeNotifications = () => {
    dropdownShow('button-notifications', 'dropdown-notifications', 'overlay-menu', false)
  }

  const executeUserProfile = () => {
    dropdownShow('button-user-profile', 'dropdown-user-profile', 'overlay-menu', false)
  }

  const executeNavbarAsideHide = () => {
    if (window.innerWidth < 992) {
      setActiveDrawer({...activeDrawer, value: 'off', aside: 'off'})
      offcanvasAsideHide('navbar-aside', 'overlay-drawer')
    }
  }

  const executeNavbarAsideMinimize = () => {
    if (asideMinimize === 'off') {
      setAsideMinimize('on')
      navBarShow('button-navbar-aside-minimize')
    } else {
      setAsideMinimize('off')
      navBarHide('button-navbar-aside-minimize')
    }
  }

  const executeOverlayTransparent = () => {
    dropdownHide('dropdown-search', 'container-search', 'overlay-menu-transparent', true)
  }

  function viewFooterNavbar(nav_item: string) {
    if (user && nav_item === 'nav-tab-companies') {
      setFooterNavbar(true)
    } else {
      setFooterNavbar(false)
    }
  }

  function toggleVisible() {
    let scrolled: number = document.documentElement.scrollTop

    if (scrolled > 200) {
      setScrollVisibility({...scrollVisibility, header: 'on', top: 'on'})
      headerShow('wrapper-header')
    }  else if (scrolled <= 200) {
      setScrollVisibility({...scrollVisibility, header: 'off', top: 'off'})
      headerHide('wrapper-header')
    }
  }

  function handleResize() {
    let width: number = window.innerWidth

    if (width > 992) {
      setActiveDrawer({...activeDrawer, value: 'off', aside: 'off'})
      offcanvasAsideHide('navbar-aside', 'overlay-drawer')
    }
  }

  useEffect( () => {
    const listNavTab: string[] = ['nav-tab-companies', 'nav-tab-dashboard', 'nav-tab-contacts', 'nav-tab-treasury', 'nav-tab-pos', 'nav-tab-sales', 'nav-tab-purchases', 'nav-tab-documents', 'nav-tab-inventory', 'nav-tab-accounting', 'nav-tab-payroll', 'nav-tab-admin']
    const listContainerAside: string[] = ['nav-tab-content-companies', 'nav-tab-content-dashborad', 'nav-tab-content-contacts', 'nav-tab-content-treasury', 'nav-tab-content-pos', 'nav-tab-content-sales', 'nav-tab-content-purchases', 'nav-tab-content-documents', 'nav-tab-content-inventory', 'nav-tab-content-accounting', 'nav-tab-content-payroll', 'nav-tab-content-admin']

    switch ((param.language) ? location.pathname.split('/')[3] : location.pathname.split('/')[2]) {
      case 'contact':
        navTabReload(listNavTab, listContainerAside, 'nav-tab-contacts', 'nav-tab-content-contacts')
        viewFooterNavbar('nav-tab-contacts')
        break;
      case 'treasury':
        navTabReload(listNavTab, listContainerAside, 'nav-tab-treasury', 'nav-tab-content-treasury')
        viewFooterNavbar('nav-tab-treasury')
        break;
      case 'pos':
        navTabReload(listNavTab, listContainerAside, 'nav-tab-pos', 'nav-tab-content-pos')
        viewFooterNavbar('nav-tab-pos')
        break;
      case 'sale':
        navTabReload(listNavTab, listContainerAside, 'nav-tab-sales', 'nav-tab-content-sales')
        viewFooterNavbar('nav-tab-sales')
        break;
      case 'purchase':
        navTabReload(listNavTab, listContainerAside, 'nav-tab-purchases', 'nav-tab-content-purchases')
        viewFooterNavbar('nav-tab-purchases')
        break;
      case 'documents':
        navTabReload(listNavTab, listContainerAside, 'nav-tab-documents', 'nav-tab-content-documents')
        viewFooterNavbar('nav-tab-documents')
        break;
      case 'inventory':
        navTabReload(listNavTab, listContainerAside, 'nav-tab-inventory', 'nav-tab-content-inventory')
        viewFooterNavbar('nav-tab-inventory')
        break;
      case 'accounting':
        navTabReload(listNavTab, listContainerAside, 'nav-tab-accounting', 'nav-tab-content-accounting')
        viewFooterNavbar('nav-tab-accounting')
        break;
      case 'payroll':
        navTabReload(listNavTab, listContainerAside, 'nav-tab-payroll', 'nav-tab-content-payroll')
        viewFooterNavbar('nav-tab-payroll')
        break;
      case 'admin':
        navTabReload(listNavTab, listContainerAside, 'nav-tab-admin', 'nav-tab-content-admin')
        viewFooterNavbar('nav-tab-admin')
        break;
      case 'company':
        if (location.pathname.includes('/company/update')) {
          navTabReload(listNavTab, listContainerAside, 'nav-tab-admin', 'nav-tab-content-admin')
          viewFooterNavbar('nav-tab-admin')
        } else {
          navTabReload(listNavTab, listContainerAside, 'nav-tab-companies', 'nav-tab-content-companies')
          viewFooterNavbar('nav-tab-companies')
        }
        break;
      default:
        navTabReload(listNavTab, listContainerAside, 'nav-tab-dashboard', 'nav-tab-content-dashborad')
        viewFooterNavbar('nav-tab-dashboard')
        break;
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, company])

  useEffect(() => {
    const tabs = Array.from(document.querySelectorAll<HTMLElement>('#nav-aside-tab'))
    if (tabs.length === 0) return

    const cleanups: Array<() => void> = []

    for (const tab of tabs) {
      const popup = tab.querySelector<HTMLElement>('#nav-aside-tab-pop-up')
      if (!popup) continue

      let animFrame: number | null = null

      const updatePosition = () => {
        const rect = tab.getBoundingClientRect()
        const centerY = rect.top + rect.height / 2
        popup.style.top = `${centerY}px`
        popup.style.setProperty('--posY', `${centerY}px`)
      }

      const updateLoop = () => {
        updatePosition()
        animFrame = requestAnimationFrame(updateLoop)
      }

      const scrollableAncestor = tab.closest<HTMLElement>('#container-aside-primary-scroll')

      const onMouseEnter = () => {
        updateLoop()

        if (scrollableAncestor) {
          scrollableAncestor.addEventListener('scroll', updatePosition, { passive: true })
        }

        window.addEventListener('scroll', updatePosition, { passive: true })
        window.addEventListener('resize', updatePosition)
      }

      const onMouseLeave = () => {
        if (animFrame)  {
          cancelAnimationFrame(animFrame)
        }

        if (scrollableAncestor) {
          scrollableAncestor.removeEventListener('scroll', updatePosition)
        }

        window.removeEventListener('scroll', updatePosition)
        window.removeEventListener('resize', updatePosition)
      }

      tab.addEventListener('mouseenter', onMouseEnter)
      tab.addEventListener('mouseleave', onMouseLeave)

      cleanups.push(() => {
        tab.removeEventListener('mouseenter', onMouseEnter)
        tab.removeEventListener('mouseleave', onMouseLeave)

        if (scrollableAncestor) {
          scrollableAncestor.removeEventListener('scroll', updatePosition)
        }

        window.removeEventListener('scroll', updatePosition)
        window.removeEventListener('resize', updatePosition)

        if (animFrame) {
          cancelAnimationFrame(animFrame)
        }
      })
    }

    return () => {
      for (const fn of cleanups) {
        fn()
      }
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asideMinimize])

  useEffect(() => {
    if (!didRun.current) {
      didRun.current = true
      loadUserLoggedIn()
    }
  }, [])

  useEffect(() => {
    if (companiesForUser && companiesForUser.length > 0) {
      setCompanies(companiesForUser)
      setMultiCompany(true)
      setCompany(authCompany())
    }
  }, [companiesForUser])

  useEffect( () => {
    window.addEventListener('scroll', toggleVisible)
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('scroll', toggleVisible)
      window.removeEventListener('resize', handleResize)
    }
  })

  return (
    <div id="navbar-aside" className="aside aside-extended" data-kt-drawer="true">
      <div id="container-aside-primary" className="aside-primary d-flex flex-column align-items-lg-center flex-row-auto">
        <div className="aside-logo d-none d-lg-flex flex-column align-items-center flex-column-auto py-5">
          <div className="cursor-pointer">
            <img src={logo} alt={lang.labels.digitalAdmin} className="h-25px" />
          </div>
        </div>
        <div className="aside-nav d-flex flex-column align-items-center flex-column-fluid w-100 pt-5 pt-lg-0" style={{minHeight: 0}}>
          <div id="container-aside-primary-scroll" className="hover-scroll-y" style={{overflowY: 'auto'}}>
            <ul className="nav flex-column">
              <li className="nav-item mb-1">
                <a id="nav-tab-companies" href="#nav-tab-content-companies" className="nav-link btn btn-custom btn-icon btn-icon-menu d-flex flex-column flex-center" data-bs-toggle="tab" onClick={(event) => executeClickOnTab(event.currentTarget.id)}>
                  <span className="svg-icon svg-icon-2x">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path opacity="0.3" d="M20 15H4C2.9 15 2 14.1 2 13V7C2 6.4 2.4 6 3 6H21C21.6 6 22 6.4 22 7V13C22 14.1 21.1 15 20 15ZM13 12H11C10.5 12 10 12.4 10 13V16C10 16.5 10.4 17 11 17H13C13.6 17 14 16.6 14 16V13C14 12.4 13.6 12 13 12Z" fill="black"/>
                      <path d="M14 6V5H10V6H8V5C8 3.9 8.9 3 10 3H14C15.1 3 16 3.9 16 5V6H14ZM20 15H14V16C14 16.6 13.5 17 13 17H11C10.5 17 10 16.6 10 16V15H4C3.6 15 3.3 14.9 3 14.7V18C3 19.1 3.9 20 5 20H19C20.1 20 21 19.1 21 18V14.7C20.7 14.9 20.4 15 20 15Z" fill="black"/>
                    </svg>
                  </span>
                  <span className="text-uppercase">{lang.labels.companies}</span>
                </a>
              </li>
              { multiCompany &&
                <>
                  <li id="nav-aside-tab" className="nav-item mb-1">
                    <a id="nav-tab-dashboard" href="#nav-tab-content-dashborad" className="nav-link btn btn-custom btn-icon btn-icon-menu d-flex flex-column flex-center" data-bs-toggle="tab" onClick={(event) => executeClickOnTab(event.currentTarget.id)}>
                      <span className="svg-icon svg-icon-2x">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="2" y="2" width="9" height="9" rx="2" fill="black" />
                          <rect opacity="0.3" x="13" y="2" width="9" height="9" rx="2" fill="black" />
                          <rect opacity="0.3" x="13" y="13" width="9" height="9" rx="2" fill="black" />
                          <rect opacity="0.3" x="2" y="13" width="9" height="9" rx="2" fill="black" />
                        </svg>
                      </span>
                      <span className="text-uppercase">{lang.labels.dashboard}</span>
                    </a>
                    { asideMinimize === 'on' &&
                      <div id="nav-aside-tab-pop-up">
                        <div className="menu menu-column menu-fit menu-rounded menu-title-gray-700 menu-icon-gray-500 menu-state-primary menu-state-icon-primary menu-state-bullet-primary menu-arrow-gray-500 bg-body p-2">
                          <div className="menu-fit">
                            <div className="menu-item">
                              <div className="menu-content">
                                <div className="separator m-1"></div>
                                <div className="separator m-1"></div>
                              </div>
                            </div>
                            <div className="menu-item">
                              <NavLink to="/main/dashboard" className={({isActive}) => `btn btn-link menu-link ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>
                                <span className="menu-icon">
                                  <span className="svg-icon svg-icon-1">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                      <rect x="2" y="2" width="9" height="9" rx="2" fill="black" />
                                      <rect opacity="0.3" x="13" y="2" width="9" height="9" rx="2" fill="black" />
                                      <rect opacity="0.3" x="13" y="13" width="9" height="9" rx="2" fill="black" />
                                      <rect opacity="0.3" x="2" y="13" width="9" height="9" rx="2" fill="black" />
                                    </svg>
                                  </span>
                                </span>
                                <span className="menu-title text-nowrap fs-4">{lang.labels.dashboard}</span>
                              </NavLink>
                            </div>
                            <div className="menu-item">
                              <div className="menu-content">
                                <div className="separator m-1"></div>
                                <div className="separator m-1"></div>
                              </div>
                            </div>
                            { user &&
                              <>
                                <div className="menu-item">
                                  <NavLink to="/main/company/view" className={({isActive}) => `menu-link ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>
                                    <span className="menu-icon">
                                      <span className="svg-icon svg-icon-2">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M11 2.375L2 9.575V20.575C2 21.175 2.4 21.575 3 21.575H9C9.6 21.575 10 21.175 10 20.575V14.575C10 13.975 10.4 13.575 11 13.575H13C13.6 13.575 14 13.975 14 14.575V20.575C14 21.175 14.4 21.575 15 21.575H21C21.6 21.575 22 21.175 22 20.575V9.575L13 2.375C12.4 1.875 11.6 1.875 11 2.375Z" fill="black"/>
                                        </svg>
                                      </span>
                                    </span>
                                    <span className="menu-title text-uppercase text-nowrap font-family-secondary fw-bolder fs-5">{lang.labels.overview}</span>
                                  </NavLink>
                                </div>
                              </>
                            }
                          </div>
                        </div>
                      </div>
                    }
                  </li>
                  <li id="nav-aside-tab" className="nav-item mb-1">
                    <a id="nav-tab-contacts" href="#nav-tab-content-contacts" className="nav-link btn btn-custom btn-icon btn-icon-menu d-flex flex-column flex-center" data-bs-toggle="tab" onClick={(event) => executeClickOnTab(event.currentTarget.id)}>
                      <span className="svg-icon svg-icon-2x">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 14H18V10H20C20.6 10 21 10.4 21 11V13C21 13.6 20.6 14 20 14ZM21 19V17C21 16.4 20.6 16 20 16H18V20H20C20.6 20 21 19.6 21 19ZM21 7V5C21 4.4 20.6 4 20 4H18V8H20C20.6 8 21 7.6 21 7Z" fill="black"/>
                          <path opacity="0.3" d="M17 22H3C2.4 22 2 21.6 2 21V3C2 2.4 2.4 2 3 2H17C17.6 2 18 2.4 18 3V21C18 21.6 17.6 22 17 22ZM10 7C8.9 7 8 7.9 8 9C8 10.1 8.9 11 10 11C11.1 11 12 10.1 12 9C12 7.9 11.1 7 10 7ZM13.3 16C14 16 14.5 15.3 14.3 14.7C13.7 13.2 12 12 10.1 12C8.10001 12 6.49999 13.1 5.89999 14.7C5.59999 15.3 6.19999 16 7.39999 16H13.3Z" fill="black"/>
                        </svg>
                      </span>
                      <span className="text-uppercase">{lang.labels.contacts}</span>
                    </a>
                    { asideMinimize === 'on' &&
                      <div id="nav-aside-tab-pop-up">
                        <div className="bg-body text-center text-uppercase font-family-secondary w-100 p-2">
                          <NavLink to="/main/contact/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.list}</NavLink>
                          <NavLink to="/main/contact/create" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.create}</NavLink>
                          <NavLink to="/main/contact/entry/none" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.accountStatements}</NavLink>
                        </div>
                      </div>
                    }
                  </li>
                  <li id="nav-aside-tab" className="nav-item mb-1">
                    <a id="nav-tab-treasury" href="#nav-tab-content-treasury" className="nav-link btn btn-custom btn-icon btn-icon-menu d-flex flex-column flex-center" data-bs-toggle="tab" onClick={(event) => executeClickOnTab(event.currentTarget.id)}>
                      <span className="svg-icon svg-icon-2x">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 19.725V18.725C20 18.125 19.6 17.725 19 17.725H5C4.4 17.725 4 18.125 4 18.725V19.725H3C2.4 19.725 2 20.125 2 20.725V21.725H22V20.725C22 20.125 21.6 19.725 21 19.725H20Z" fill="black"/>
                          <path opacity="0.3" d="M22 6.725V7.725C22 8.325 21.6 8.725 21 8.725H18C18.6 8.725 19 9.125 19 9.725C19 10.325 18.6 10.725 18 10.725V15.725C18.6 15.725 19 16.125 19 16.725V17.725H15V16.725C15 16.125 15.4 15.725 16 15.725V10.725C15.4 10.725 15 10.325 15 9.725C15 9.125 15.4 8.725 16 8.725H13C13.6 8.725 14 9.125 14 9.725C14 10.325 13.6 10.725 13 10.725V15.725C13.6 15.725 14 16.125 14 16.725V17.725H10V16.725C10 16.125 10.4 15.725 11 15.725V10.725C10.4 10.725 10 10.325 10 9.725C10 9.125 10.4 8.725 11 8.725H8C8.6 8.725 9 9.125 9 9.725C9 10.325 8.6 10.725 8 10.725V15.725C8.6 15.725 9 16.125 9 16.725V17.725H5V16.725C5 16.125 5.4 15.725 6 15.725V10.725C5.4 10.725 5 10.325 5 9.725C5 9.125 5.4 8.725 6 8.725H3C2.4 8.725 2 8.325 2 7.725V6.725L11 2.225C11.6 1.925 12.4 1.925 13.1 2.225L22 6.725ZM12 3.725C11.2 3.725 10.5 4.425 10.5 5.225C10.5 6.025 11.2 6.725 12 6.725C12.8 6.725 13.5 6.025 13.5 5.225C13.5 4.425 12.8 3.725 12 3.725Z" fill="black"/>
                        </svg>
                      </span>
                      <span className="text-uppercase">{lang.labels.treasury}</span>
                    </a>
                    { asideMinimize === 'on' &&
                      <div id="nav-aside-tab-pop-up">
                        <div className="bg-body text-center text-uppercase font-family-secondary w-100 p-2">
                          <NavLink to="/main/treasury/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.list}</NavLink>
                          <NavLink to="/main/treasury/create" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.create}</NavLink>
                          <NavLink to="/main/treasury/bank-statement/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.bankStatements}</NavLink>
                          <NavLink to="/main/treasury/bank-statement/transactions" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.bankTransactions}</NavLink>
                        </div>
                      </div>
                    }
                  </li>
                  <li id="nav-aside-tab" className="nav-item mb-1">
                    <a id="nav-tab-pos" href="#nav-tab-content-pos" className="nav-link btn btn-custom btn-icon btn-icon-menu d-flex flex-column flex-center" data-bs-toggle="tab" onClick={(event) => executeClickOnTab(event.currentTarget.id)}>
                      <span className="svg-icon svg-icon-2x">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path opacity="0.3" d="M3 13V11C3 10.4 3.4 10 4 10H20C20.6 10 21 10.4 21 11V13C21 13.6 20.6 14 20 14H4C3.4 14 3 13.6 3 13Z" fill="black"/>
                          <path d="M13 21H11C10.4 21 10 20.6 10 20V4C10 3.4 10.4 3 11 3H13C13.6 3 14 3.4 14 4V20C14 20.6 13.6 21 13 21Z" fill="black"/>
                          <path d="M20 21H18C17.4 21 17 20.6 17 20V4C17 3.4 17.4 3 18 3H20C20.6 3 21 3.4 21 4V20C21 20.6 20.6 21 20 21Z" fill="black"/>
                          <path d="M6 21H4C3.4 21 3 20.6 3 20V4C3 3.4 3.4 3 4 3H6C6.6 3 7 3.4 7 4V20C7 20.6 6.6 21 6 21Z" fill="black"/>
                        </svg>
                      </span>
                      <span className="text-uppercase">{lang.labels.pos}</span>
                    </a>
                    { asideMinimize === 'on' &&
                      <div id="nav-aside-tab-pop-up">
                        <div className="bg-body text-center text-uppercase font-family-secondary w-100 p-2">
                          <NavLink to="/main/pos/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.list}</NavLink>
                          <NavLink to="/main/pos/create" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.create}</NavLink>
                        </div>
                      </div>
                    }
                  </li>
                  <li id="nav-aside-tab" className="nav-item mb-1">
                    <a id="nav-tab-sales" href="#nav-tab-content-sales" className="nav-link btn btn-custom btn-icon btn-icon-menu d-flex flex-column flex-center" data-bs-toggle="tab" onClick={(event) => executeClickOnTab(event.currentTarget.id)}>
                      <span className="svg-icon svg-icon-2x">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path opacity="0.3" d="M11 11H13C13.6 11 14 11.4 14 12V21H10V12C10 11.4 10.4 11 11 11ZM16 3V21H20V3C20 2.4 19.6 2 19 2H17C16.4 2 16 2.4 16 3Z" fill="black"/>
                          <path d="M21 20H8V16C8 15.4 7.6 15 7 15H5C4.4 15 4 15.4 4 16V20H3C2.4 20 2 20.4 2 21C2 21.6 2.4 22 3 22H21C21.6 22 22 21.6 22 21C22 20.4 21.6 20 21 20Z" fill="black"/>
                        </svg>
                      </span>
                      <span className="text-uppercase">{lang.labels.sales}</span>
                    </a>
                    { asideMinimize === 'on' &&
                      <div id="nav-aside-tab-pop-up">
                        <div className="bg-body text-center text-uppercase font-family-secondary w-100 p-2">
                          <NavLink to="/main/sale/quote/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.quotes}</NavLink>
                          <NavLink to="/main/sale/order/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.orders}</NavLink>
                          <NavLink to="/main/sale/invoice/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.invoices}</NavLink>
                          <NavLink to="/main/sale/recurring-contract/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.recurringContracts}</NavLink>
                        </div>
                      </div>
                    }
                  </li>
                  <li id="nav-aside-tab" className="nav-item mb-1">
                    <a id="nav-tab-purchases" href="#nav-tab-content-purchases" className="nav-link btn btn-custom btn-icon btn-icon-menu d-flex flex-column flex-center" data-bs-toggle="tab" onClick={(event) => executeClickOnTab(event.currentTarget.id)}>
                      <span className="svg-icon svg-icon-2x">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path opacity="0.3" d="M14 12V21H10V12C10 11.4 10.4 11 11 11H13C13.6 11 14 11.4 14 12ZM7 2H5C4.4 2 4 2.4 4 3V21H8V3C8 2.4 7.6 2 7 2Z" fill="black"/>
                          <path d="M21 20H20V16C20 15.4 19.6 15 19 15H17C16.4 15 16 15.4 16 16V20H3C2.4 20 2 20.4 2 21C2 21.6 2.4 22 3 22H21C21.6 22 22 21.6 22 21C22 20.4 21.6 20 21 20Z" fill="black"/>
                        </svg>
                      </span>
                      <span className="text-uppercase">{lang.labels.purchases}</span>
                    </a>
                    { asideMinimize === 'on' &&
                      <div id="nav-aside-tab-pop-up">
                        <div className="bg-body text-center text-uppercase font-family-secondary w-100 p-2">
                          <NavLink to="/main/purchase/quote/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.providerQuotes}</NavLink>
                          <NavLink to="/main/purchase/order/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.providerOrders}</NavLink>
                          <NavLink to="/main/purchase/invoice/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.providerInvoices}</NavLink>
                          <NavLink to="/main/purchase/expense/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.expenses}</NavLink>
                          <NavLink to="/main/purchase/expense-report/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.expenseReports}</NavLink>
                          <NavLink to="/main/purchase/consignment/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.consignments}</NavLink>
                        </div>
                      </div>
                    }
                  </li>
                  <li id="nav-aside-tab" className="nav-item mb-1">
                    <Link id="nav-tab-documents" to="/main/documents/list" className="nav-link btn btn-custom btn-icon btn-icon-menu d-flex flex-column flex-center" onClick={() => { executeClickOnTab('nav-tab-documents'); executeNavbarAsideHide() }}>
                      <span className="svg-icon svg-icon-2x">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22Z" fill="black"/>
                          <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black"/>
                        </svg>
                      </span>
                      <span className="text-uppercase">{lang.labels.documents}</span>
                    </Link>
                    { asideMinimize === 'on' &&
                      <div id="nav-aside-tab-pop-up">
                        <div className="bg-body text-center text-uppercase font-family-secondary w-100 p-2">
                          <NavLink to="/main/documents/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.listDocuments}</NavLink>
                          <Link to="/main/documents/list?upload=1" className="btn btn-primary text-nowrap w-100 my-1" onClick={executeNavbarAsideHide}>{lang.labels.uploadDocument}</Link>
                        </div>
                      </div>
                    }
                  </li>
                  <li id="nav-aside-tab" className="nav-item mb-1">
                    <a id="nav-tab-inventory" href="#nav-tab-content-inventory" className="nav-link btn btn-custom btn-icon btn-icon-menu d-flex flex-column flex-center" data-bs-toggle="tab" onClick={(event) => executeClickOnTab(event.currentTarget.id)}>
                      <span className="svg-icon svg-icon-2x">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path opacity="0.3" d="M5 8.04999L11.8 11.95V19.85L5 15.85V8.04999Z" fill="black"/>
                          <path d="M20.1 6.65L12.3 2.15C12 1.95 11.6 1.95 11.3 2.15L3.5 6.65C3.2 6.85 3 7.15 3 7.45V16.45C3 16.75 3.2 17.15 3.5 17.25L11.3 21.75C11.5 21.85 11.6 21.85 11.8 21.85C12 21.85 12.1 21.85 12.3 21.75L20.1 17.25C20.4 17.05 20.6 16.75 20.6 16.45V7.45C20.6 7.15 20.4 6.75 20.1 6.65ZM5 15.85V7.95L11.8 4.05L18.6 7.95L11.8 11.95V19.85L5 15.85Z" fill="black"/>
                        </svg>
                      </span>
                      <span className="text-uppercase">{lang.labels.inventory}</span>
                    </a>
                    { asideMinimize === 'on' &&
                      <div id="nav-aside-tab-pop-up">
                        <div className="bg-body text-center text-uppercase font-family-secondary w-100 p-2">
                          <NavLink to="/main/inventory/category/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.categories}</NavLink>
                          <NavLink to="/main/inventory/service/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.services}</NavLink>
                          <NavLink to="/main/inventory/product/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.products}</NavLink>
                          <NavLink to="/main/inventory/storage/list" className={({ isActive }) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.storageCenter}</NavLink>
                          <NavLink to="/main/inventory/asset/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.assets}</NavLink>
                          <NavLink to="/main/inventory/brand/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.brands}</NavLink>
                        </div>
                      </div>
                    }
                  </li>
                  <li id="nav-aside-tab" className="nav-item mb-1">
                    <a id="nav-tab-accounting" href="#nav-tab-content-accounting" className="nav-link btn btn-custom btn-icon btn-icon-menu d-flex flex-column flex-center" data-bs-toggle="tab" onClick={(event) => executeClickOnTab(event.currentTarget.id)}>
                      <span className="svg-icon svg-icon-2x">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path opacity="0.3" d="M3 3V17H7V21H15V9H20V3H3Z" fill="black"/>
                          <path d="M20 22H3C2.4 22 2 21.6 2 21V3C2 2.4 2.4 2 3 2H20C20.6 2 21 2.4 21 3V21C21 21.6 20.6 22 20 22ZM19 4H4V8H19V4ZM6 18H4V20H6V18ZM6 14H4V16H6V14ZM6 10H4V12H6V10ZM10 18H8V20H10V18ZM10 14H8V16H10V14ZM10 10H8V12H10V10ZM14 18H12V20H14V18ZM14 14H12V16H14V14ZM14 10H12V12H14V10ZM19 14H17V20H19V14ZM19 10H17V12H19V10Z" fill="black"/>
                        </svg>
                      </span>
                      <span className="text-uppercase">{lang.labels.accounting}</span>
                    </a>
                    { asideMinimize === 'on' &&
                      <div id="nav-aside-tab-pop-up">
                        <div className="bg-body text-center text-uppercase font-family-secondary w-100 p-2">
                          <NavLink to="/main/accounting/journal/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.bookingJournals}</NavLink>
                          <NavLink to="/main/accounting/plan/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.chartAccounts}</NavLink>
                          <NavLink to="/main/accounting/plan/account" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.accountingAccounts}</NavLink>
                          <NavLink to="/main/accounting/fiscal-year/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.fiscalYears}</NavLink>
                          <NavLink to="/main/accounting/statement/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.vatStatement}</NavLink>
                          <NavLink to="/main/accounting/reports/balance-sheet" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.balanceSheet}</NavLink>
                          <NavLink to="/main/accounting/reports/income-statement" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.incomeStatement}</NavLink>
                          <NavLink to="/main/accounting/reports/trial-balance" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.trialBalance}</NavLink>

                        </div>
                      </div>
                    }
                  </li>
                  { payrollAccess.mode === 'normal' &&
                    <li id="nav-aside-tab" className="nav-item mb-1">
                      <a id="nav-tab-payroll" href="#nav-tab-content-payroll" className="nav-link btn btn-custom btn-icon btn-icon-menu d-flex flex-column flex-center" data-bs-toggle="tab" onClick={(event) => executeClickOnTab(event.currentTarget.id)}>
                        <span className="svg-icon svg-icon-2x">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path opacity="0.3" d="M20 15H4C2.9 15 2 14.1 2 13V7C2 5.9 2.9 5 4 5H20C21.1 5 22 5.9 22 7V13C22 14.1 21.1 15 20 15Z" fill="black"/>
                            <path d="M14 6V4H10V6H8V4C8 2.9 8.9 2 10 2H14C15.1 2 16 2.9 16 4V6H14ZM20 15H4C2.9 15 2 14.1 2 13V11C3.1 11 4 11.9 4 13H10V12H14V13H20C20 11.9 20.9 11 22 11V13C22 14.1 21.1 15 20 15ZM14 12V13H10V12H14ZM2 13V19C2 20.1 2.9 21 4 21H20C21.1 21 22 20.1 22 19V13C20.9 13 20 13.9 20 15H14V16H10V15H4C4 13.9 3.1 13 2 13Z" fill="black"/>
                          </svg>
                        </span>
                        <span className="text-uppercase">{lang.labels.payrollShort}</span>
                      </a>
                      { asideMinimize === 'on' &&
                        <div id="nav-aside-tab-pop-up">
                          <div className="bg-body text-center text-uppercase font-family-secondary w-100 p-2">
                            <NavLink to="/main/payroll/employees/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.employees}</NavLink>
                            <NavLink to="/main/payroll/runs/list" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.payrollRuns}</NavLink>
                          </div>
                        </div>
                      }
                    </li>
                  }
                  { payrollAccess.mode === 'disabled' &&
                    <li id="nav-aside-tab" className="nav-item mb-1">
                      <a
                        className="nav-link btn btn-custom btn-icon btn-icon-menu d-flex flex-column flex-center opacity-50 cursor-pointer"
                        title={payrollAccess.availableAsAddon ? lang.labels.navFeatureAvailableAsAddon : lang.labels.navFeatureRequiresUpgrade}
                        onClick={() => payrollAccess.availableAsAddon ? navigate('/main/admin/subscription/addons') : openUpgradeModal('payroll')}
                      >
                        <span className="svg-icon svg-icon-2x">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path opacity="0.3" d="M20 15H4C2.9 15 2 14.1 2 13V7C2 5.9 2.9 5 4 5H20C21.1 5 22 5.9 22 7V13C22 14.1 21.1 15 20 15Z" fill="black"/>
                            <path d="M14 6V4H10V6H8V4C8 2.9 8.9 2 10 2H14C15.1 2 16 2.9 16 4V6H14ZM20 15H4C2.9 15 2 14.1 2 13V11C3.1 11 4 11.9 4 13H10V12H14V13H20C20 11.9 20.9 11 22 11V13C22 14.1 21.1 15 20 15ZM14 12V13H10V12H14ZM2 13V19C2 20.1 2.9 21 4 21H20C21.1 21 22 20.1 22 19V13C20.9 13 20 13.9 20 15H14V16H10V15H4C4 13.9 3.1 13 2 13Z" fill="black"/>
                          </svg>
                        </span>
                        <span className="text-uppercase">{lang.labels.payrollShort}</span>
                      </a>
                    </li>
                  }
                  <li id="nav-aside-tab" className="nav-item mb-1">
                    <a id="nav-tab-admin" href="#nav-tab-content-admin" className="nav-link btn btn-custom btn-icon btn-icon-menu d-flex flex-column flex-center" data-bs-toggle="tab" onClick={(event) => executeClickOnTab(event.currentTarget.id)}>
                      <span className="svg-icon svg-icon-2x">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6.28548 15.0861C7.34369 13.1814 9.35142 12 11.5304 12H12.4696C14.6486 12 16.6563 13.1814 17.7145 15.0861L19.3493 18.0287C20.0899 19.3618 19.1259 21 17.601 21H6.39903C4.87406 21 3.91012 19.3618 4.65071 18.0287L6.28548 15.0861Z" fill="black"/>
                          <rect opacity="0.3" x="8" y="3" width="8" height="8" rx="4" fill="black"/>
                        </svg>
                      </span>
                      <span className="text-uppercase">{lang.labels.admin}</span>
                    </a>
                    { asideMinimize === 'on' &&
                      <div id="nav-aside-tab-pop-up">
                        <div className="bg-body text-center text-uppercase font-family-secondary w-100 p-2">
                          <NavLink to="/main/company/update" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.settings}</NavLink>
                          {showSubscriptionMenu && <NavLink to="/main/admin/subscription" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.subscription}</NavLink>}
                          <NavLink to="/main/admin/users" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.users}</NavLink>
                          <NavLink to="/main/admin/invitations" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.invitations}</NavLink>
                          <NavLink to="/main/admin/roles" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.roles}</NavLink>
                          <NavLink to="/main/admin/policies" className={({isActive}) => `btn btn-primary text-nowrap w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.policies}</NavLink>
                        </div>
                      </div>
                    }
                  </li>
                </>
              }
            </ul>
          </div>
        </div>
        { user &&
          <div className="aside-footer d-flex flex-column align-items-center flex-column-auto" onLoad={uploadTooltip}>
            <div className="d-flex align-items-center mb-1" data-bs-toggle="tooltip" data-bs-placement="right" title={lang.labels.notifications} onClick={removeTooltip}>
              <div id="button-notifications" className="btn btn-icon btn-custom position-relative" onClick={executeNotifications}>
                { notificationsUnread > 0 &&
                  <span className="position-absolute top-25 start-75 translate-middle bg-warning border border-light rounded-circle p-1"></span>
                }
                <span className="svg-icon svg-icon-1 svg-icon-lg-2x">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 8.725C6 8.125 6.4 7.725 7 7.725H14L18 11.725V12.925L22 9.725L12.6 2.225C12.2 1.925 11.7 1.925 11.4 2.225L2 9.725L6 12.925V8.725Z" fill="black"/>
                    <path opacity="0.3" d="M22 9.72498V20.725C22 21.325 21.6 21.725 21 21.725H3C2.4 21.725 2 21.325 2 20.725V9.72498L11.4 17.225C11.8 17.525 12.3 17.525 12.6 17.225L22 9.72498ZM15 11.725H18L14 7.72498V10.725C14 11.325 14.4 11.725 15 11.725Z" fill="black"/>
                  </svg>
                </span>
              </div>
            </div>
            {chatAllowed && (
              <div className="d-flex align-items-center mb-1" data-bs-toggle="tooltip" data-bs-placement="right" title={lang.labels.chatTitle} onClick={removeTooltip}>
                <div className="btn btn-icon btn-custom position-relative" onClick={() => openChat({})}>
                  {chatUnread > 0 && (
                    <span className="position-absolute top-25 start-75 translate-middle bg-danger border border-light rounded-circle p-1"></span>
                  )}
                  <span className="svg-icon svg-icon-1 svg-icon-lg-2x">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path opacity="0.3" d="M20 3H4C2.89543 3 2 3.89543 2 5V16C2 17.1046 2.89543 18 4 18H4.5C5.05228 18 5.5 18.4477 5.5 19V21.5052C5.5 22.1441 6.21212 22.5253 6.74376 22.1708L11.4885 19.0077C12.4741 18.3506 13.6321 18 14.8167 18H20C21.1046 18 22 17.1046 22 16V5C22 3.89543 21.1046 3 20 3Z" fill="currentColor"/>
                      <rect x="6" y="12" width="7" height="2" rx="1" fill="currentColor"/>
                      <rect x="6" y="7" width="12" height="2" rx="1" fill="currentColor"/>
                    </svg>
                  </span>
                </div>
              </div>
            )}
            <div className="d-flex align-items-center mb-3" data-bs-toggle="tooltip" data-bs-placement="right" title={lang.labels.userProfile} onClick={removeTooltip}>
              <div id="button-user-profile" className="cursor-pointer symbol symbol-40px" onClick={executeUserProfile}>
                <img src={imageUser(user.avatar)} alt={lang.labels.avatar} />
              </div>
            </div>
          </div>
        }
      </div>
      <div id="container-aside-secondary" className="aside-secondary d-flex flex-row-fluid">
        <div className="aside-workspace p-2 pe-3">
          <div className="d-flex flex-column h-100">
            <div id="container-aside-secondary-scroll" className="flex-column-fluid hover-scroll-y w-100">
              <div className="tab-content">
                <div id="nav-tab-content-companies" className="tab-pane fade" role="tabpanel">
                  <div className="d-flex mb-5">
                    <div id="dropdown-search" className="menu-dropdown d-flex align-items-center w-100 w-lg-400px">
                      <div id="container-search-form" className="w-100 position-relative mb-5 mb-lg-0">
                        <span className="svg-icon svg-icon-2 svg-icon-lg-3 svg-icon-gray-800 position-absolute top-50 translate-middle-y ms-3">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="black" />
                            <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="black" />
                          </svg>
                        </span>
                        <input className="form-control px-10" type="text" autoComplete="off" name="search" placeholder={lang.labels.search} value={searchData.text} onChange={handleChangeSearch} />
                        <span id="icon-spinner" className={`position-absolute top-50 end-0 translate-middle-y lh-0 me-2 ${searchAnimation.spinner === false && "d-none"}`}>
                          <span className="spinner-border h-15px w-15px align-middle text-muted"></span>
                        </span>
                        <span id="button-clear" className={`btn btn-flush btn-active-color-primary position-absolute top-50 end-0 translate-middle-y lh-0 me-2 ${searchAnimation.clear === false && "d-none"}`} onClick={executeClearSearch}>
                          <span className="svg-icon svg-icon-2 svg-icon-lg-1 me-0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="black" />
                              <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="black" />
                            </svg>
                          </span>
                        </span>
                      </div>
                      <div id="container-search" className="menu menu-sub menu-sub-dropdown overflow-hidden p-5 w-300px w-md-350px">
                        <Container property={(searchData.list_companies.length > 0).toString()}>
                          { searchData.list_app_services.length > 0 && searchData.list_app_services.map (( (item, index) => {
                            return (
                              <div key={index} className="d-flex align-items-center mb-3">
                                <div className="fs-6 text-uppercase fw-bolder">{item.name}</div>
                                <span className="badge badge-light-primary fs-9 ms-3"><small className="text-uppercase">{lang.labels.appService}</small></span>
                              </div>
                            )
                          }))}
                          <div className="d-flex flex-stack fw-bolder mb-5">
                            <span className="text-muted fs-6">{lang.labels.searchResults}:</span>
                          </div>
                          <div className="scroll-y mh-200px mh-lg-325px">
                            { searchData.list_companies.length > 0 && searchData.list_companies.map (( (item, index) => {
                              return (
                                <div key={index} onClick={() => submitCompanySelectedInSearch(item)}>
                                  <div className="d-flex align-items-center mb-3 cursor-pointer">
                                    <div className="symbol symbol-35px me-3">
                                      <span className="symbol-label bg-light">
                                        <img src={imageCompany(item.logo)} alt={lang.labels.logo} className="w-50 h-50 align-self-center" />
                                      </span>
                                    </div>
                                    <div className="d-flex flex-column">
                                      <div className="fs-7 text-hover-primary fw-bold">{item.name}</div>
                                      <span className="fs-8 text-muted fw-bold">{item.address.city}, {countryName(lang.code, item.address.country)}</span>
                                    </div>
                                  </div>
                                </div>
                              )
                            }))}
                          </div>
                        </Container>
                        <Container property={(searchData.list_companies.length === 0).toString()}>
                          <div className="text-center">
                            <div className="py-10">
                              <span className="svg-icon svg-icon-5x opacity-50">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                  <path opacity="0.3" d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z" fill="black" />
                                  <path d="M20 8L14 2V6C14 7.10457 14.8954 8 16 8H20Z" fill="black" />
                                  <rect x="13.6993" y="13.6656" width="4.42828" height="1.73089" rx="0.865447" transform="rotate(45 13.6993 13.6656)" fill="black" />
                                  <path d="M15 12C15 14.2 13.2 16 11 16C8.8 16 7 14.2 7 12C7 9.8 8.8 8 11 8C13.2 8 15 9.8 15 12ZM11 9.6C9.68 9.6 8.6 10.68 8.6 12C8.6 13.32 9.68 14.4 11 14.4C12.32 14.4 13.4 13.32 13.4 12C13.4 10.68 12.32 9.6 11 9.6Z" fill="black" />
                                </svg>
                              </span>
                            </div>
                            <div className="pb-10 fw-bold">
                              <h3 className="text-muted fs-5 mb-2">{lang.labels.noResultFound}</h3>
                              <div className="text-muted fs-7">{lang.labels.pleaseTryAgainWithADifferentQuery}</div>
                            </div>
                          </div>
                        </Container>
                      </div>
                    </div>
                  </div>
                  <div className="m-0">
                    <h1 className="text-uppercase font-family-secondary mx-3 mb-5">{lang.labels.companies}</h1>
                    <div className="w-100 h-100">
                      { companies && companies.map (( (item, index) => {
                        return (
                          <div key={index} onClick={() => submitCompanySelected(item)} className={item.id === company?.id ? "bg-light-primary" : "bg-tranpsarent"}>
                            <div className="custom-list d-flex align-items-center p-3 cursor-pointer" >
                              <div className="symbol symbol-40px me-5">
                                <span className="symbol-label">
                                  <img src={imageCompany(item.logo)} alt={lang.labels.logo} className="mw-75 h-auto align-self-center" />
                                </span>
                              </div>
                              <div className="d-flex flex-column flex-grow-1">
                                <span className="custom-list-title text-hover-primary fw-bolder mb-1">{item.name}</span>
                                <small className="text-muted fw-bold">{formatLocation(lang, item.address)}</small>
                              </div>
                            </div>
                          </div>
                        )
                      }))}
                    </div>
                  </div>
                </div>
                { multiCompany &&
                  <>
                    <div id="nav-tab-content-dashborad" className="tab-pane fade" role="tabpanel">
                      <div className="menu menu-column menu-fit menu-rounded menu-title-gray-700 menu-icon-gray-500 menu-state-primary menu-state-icon-primary menu-state-bullet-primary menu-arrow-gray-500" data-kt-menu="true">
                        <div className="menu-fit">
                          <div className="menu-item">
                            <div className="menu-content">
                              <div className="separator m-1"></div>
                              <div className="separator m-1"></div>
                            </div>
                          </div>
                          <div className="menu-item">
                            <NavLink to="/main/dashboard" className={({isActive}) => `btn btn-link menu-link ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>
                              <span className="menu-icon">
                                <span className="svg-icon svg-icon-1">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <rect x="2" y="2" width="9" height="9" rx="2" fill="black" />
                                    <rect opacity="0.3" x="13" y="2" width="9" height="9" rx="2" fill="black" />
                                    <rect opacity="0.3" x="13" y="13" width="9" height="9" rx="2" fill="black" />
                                    <rect opacity="0.3" x="2" y="13" width="9" height="9" rx="2" fill="black" />
                                  </svg>
                                </span>
                              </span>
                              <span className="menu-title fs-4">{lang.labels.dashboard}</span>
                            </NavLink>
                          </div>
                          <div className="menu-item">
                            <div className="menu-content">
                              <div className="separator m-1"></div>
                              <div className="separator m-1"></div>
                            </div>
                          </div>
                          { user &&
                            <>
                              <div className="menu-item">
                                <NavLink to="/main/company/view" className={({isActive}) => `menu-link ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>
                                  <span className="menu-icon">
                                    <span className="svg-icon svg-icon-2">
                                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M11 2.375L2 9.575V20.575C2 21.175 2.4 21.575 3 21.575H9C9.6 21.575 10 21.175 10 20.575V14.575C10 13.975 10.4 13.575 11 13.575H13C13.6 13.575 14 13.975 14 14.575V20.575C14 21.175 14.4 21.575 15 21.575H21C21.6 21.575 22 21.175 22 20.575V9.575L13 2.375C12.4 1.875 11.6 1.875 11 2.375Z" fill="black"/>
                                      </svg>
                                    </span>
                                  </span>
                                  <span className="menu-title text-uppercase font-family-secondary fw-bolder fs-5">{lang.labels.overview}</span>
                                </NavLink>
                              </div>
                            </>
                          }
                          <div className="text-center my-10 mx-5">
                            <img src={imgDashboard} className="mw-100 mh-200px" alt={lang.labels.dashboard} />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div id="nav-tab-content-contacts" className="tab-pane fade" role="tabpanel">
                      <div className="text-center pt-5 mb-10">
                        <h2 className="text-uppercase font-family-secondary mb-5">{lang.labels.contacts}</h2>
                        <p className="text-muted fw-bold mb-10">
                          {lang.labels.selectOneOption}
                          <br/>
                          {lang.labels.youWantToRun}
                        </p>
                        <div className="text-uppercase font-family-secondary w-100">
                          <NavLink to="/main/contact/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.list}</NavLink>
                          <NavLink to="/main/contact/create" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.create}</NavLink>
                          <NavLink to="/main/contact/entry/none" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.accountStatements}</NavLink>
                        </div>
                      </div>
                      <div className="text-center px-5">
                        <img src={imgContacts} className="mw-100 mh-200px" alt={lang.labels.contacts} />
                      </div>
                    </div>
                    <div id="nav-tab-content-treasury" className="tab-pane fade" role="tabpanel">
                      <div className="text-center pt-5 mb-10">
                        <h2 className="text-uppercase font-family-secondary mb-5">{lang.labels.treasuryAccounts}</h2>
                        <p className="text-muted fw-bold mb-10">
                          {lang.labels.selectOneOption}
                          <br/>
                          {lang.labels.youWantToRun}
                        </p>
                        <div className="text-uppercase font-family-secondary w-100">
                          <NavLink to="/main/treasury/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.list}</NavLink>
                          <NavLink to="/main/treasury/create" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.create}</NavLink>
                          <NavLink to="/main/treasury/bank-statement/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.bankStatements}</NavLink>
                          <NavLink to="/main/treasury/bank-statement/transactions" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.bankTransactions}</NavLink>
                        </div>
                      </div>
                      <div className="text-center px-5">
                        <img src={imgTreasury} className="mw-100 mh-200px" alt={lang.labels.treasury} />
                      </div>
                    </div>
                    <div id="nav-tab-content-pos" className="tab-pane fad" role="tabpanel">
                      <div className="text-center pt-5 mb-10">
                        <h2 className="text-uppercase font-family-secondary mb-5">{lang.labels.pos}</h2>
                        <p className="text-muted fw-bold mb-10">
                          {lang.labels.selectOneOption}
                          <br/>
                          {lang.labels.youWantToRun}
                        </p>
                        <div className="text-uppercase font-family-secondary w-100">
                          <NavLink to="/main/pos/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.list}</NavLink>
                          <NavLink to="/main/pos/create" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.create}</NavLink>
                        </div>
                      </div>
                      <div className="text-center px-5">
                        <img src={imgPos} alt={lang.labels.pos} className="mw-100 mh-200px" />
                      </div>
                    </div>
                    <div id="nav-tab-content-sales" className="tab-pane fad" role="tabpanel">
                      <div className="text-center pt-5 mb-10">
                        <h2 className="text-uppercase font-family-secondary mb-5">{lang.labels.sales}</h2>
                        <p className="text-muted fw-bold mb-10">
                          {lang.labels.selectOneOption}
                          <br/>
                          {lang.labels.youWantToRun}
                        </p>
                        <div className="text-uppercase font-family-secondary w-100">
                          <NavLink to="/main/sale/quote/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.quotes}</NavLink>
                          <NavLink to="/main/sale/order/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.orders}</NavLink>
                          <NavLink to="/main/sale/invoice/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.invoices}</NavLink>
                          <NavLink to="/main/sale/recurring-contract/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.recurringContracts}</NavLink>
                        </div>
                      </div>
                      <div className="text-center px-5">
                        <img src={imgSales} alt={lang.labels.sales} className="mw-100 mh-200px" />
                      </div>
                    </div>
                    <div id="nav-tab-content-purchases" className="tab-pane fad" role="tabpanel">
                      <div className="text-center pt-5 mb-10">
                        <h2 className="text-uppercase font-family-secondary mb-5">{lang.labels.purchases}</h2>
                        <p className="text-muted fw-bold mb-10">
                          {lang.labels.selectOneOption}
                          <br/>
                          {lang.labels.youWantToRun}
                        </p>
                        <div className="text-uppercase font-family-secondary w-100">
                          <NavLink to="/main/purchase/quote/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.providerQuotes}</NavLink>
                          <NavLink to="/main/purchase/order/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.providerOrders}</NavLink>
                          <NavLink to="/main/purchase/invoice/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.providerInvoices}</NavLink>
                          <NavLink to="/main/purchase/expense/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.expenses}</NavLink>
                          <NavLink to="/main/purchase/expense-report/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.expenseReports}</NavLink>
                          <NavLink to="/main/purchase/consignment/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.consignments}</NavLink>
                        </div>
                      </div>
                      <div className="text-center px-5">
                        <img src={imgPurchases} alt={lang.labels.purchases} className="mw-100 mh-200px" />
                      </div>
                    </div>
                    <div id="nav-tab-content-documents" className="tab-pane fade" role="tabpanel">
                      <div className="text-center pt-5 mb-10">
                        <h2 className="text-uppercase font-family-secondary mb-5">{lang.labels.documents}</h2>
                        <p className="text-muted fw-bold mb-10">
                          {lang.labels.selectOneOption}
                          <br/>
                          {lang.labels.youWantToRun}
                        </p>
                        <div className="text-uppercase font-family-secondary w-100">
                          <NavLink to="/main/documents/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.listDocuments}</NavLink>
                          <Link to="/main/documents/list?upload=1" className="btn btn-primary w-100 my-1" onClick={executeNavbarAsideHide}>{lang.labels.uploadDocument}</Link>
                        </div>
                      </div>
                      <div className="text-center px-5">
                        <img src={imgDocuments} className="mw-100 mh-200px" alt={lang.labels.documents} />
                      </div>
                    </div>
                    <div id="nav-tab-content-inventory" className="tab-pane fade"  role="tabpanel">
                      <div className="text-center pt-5 mb-10">
                        <h2 className="text-uppercase font-family-secondary mb-5">{lang.labels.inventory}</h2>
                        <p className="text-muted fw-bold mb-10">
                          {lang.labels.selectOneOption}
                          <br/>
                          {lang.labels.youWantToRun}
                        </p>
                        <div className="text-uppercase font-family-secondary w-100">
                          <NavLink to="/main/inventory/category/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.categories}</NavLink>
                          <NavLink to="/main/inventory/service/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.services}</NavLink>
                          <NavLink to="/main/inventory/product/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.products}</NavLink>
                          <NavLink to="/main/inventory/storage/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.storageCenter}</NavLink>
                          <NavLink to="/main/inventory/asset/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.assets}</NavLink>
                          <NavLink to="/main/inventory/brand/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.brands}</NavLink>
                        </div>
                      </div>
                      <div className="text-center px-5">
                        <img src={imgInventory} alt={lang.labels.inventory} className="mw-100 mh-200px" />
                      </div>
                    </div>
                    <div id="nav-tab-content-accounting" className="tab-pane fade"  role="tabpanel">
                      <div className="text-center pt-5 mb-10">
                        <h2 className="text-uppercase font-family-secondary mb-5">{lang.labels.accounting}</h2>
                        <p className="text-muted fw-bold mb-10">
                          {lang.labels.selectOneOption}
                          <br/>
                          {lang.labels.youWantToRun}
                        </p>
                        <div className="text-uppercase font-family-secondary w-100">
                          <NavLink to="/main/accounting/journal/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.bookingJournals}</NavLink>
                          <NavLink to="/main/accounting/plan/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.chartAccounts}</NavLink>
                          <NavLink to="/main/accounting/plan/account" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.accountingAccounts}</NavLink>
                          <NavLink to="/main/accounting/fiscal-year/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.fiscalYears}</NavLink>
                          <NavLink to="/main/accounting/statement/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.vatStatement}</NavLink>
                          <NavLink to="/main/accounting/reports/balance-sheet" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.balanceSheet}</NavLink>
                          <NavLink to="/main/accounting/reports/income-statement" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.incomeStatement}</NavLink>
                          <NavLink to="/main/accounting/reports/trial-balance" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.trialBalance}</NavLink>

                        </div>
                      </div>
                      <div className="text-center px-5">
                        <img src={imgAccounting} alt={lang.labels.accounting} className="mw-100 mh-200px" />
                      </div>
                    </div>
                    { payrollAccess.mode === 'normal' &&
                      <div id="nav-tab-content-payroll" className="tab-pane fade" role="tabpanel">
                        <div className="text-center pt-5 mb-10">
                          <h2 className="text-uppercase font-family-secondary mb-5">{lang.labels.payroll}</h2>
                          <p className="text-muted fw-bold mb-10">
                            {lang.labels.selectOneOption}
                            <br/>
                            {lang.labels.youWantToRun}
                          </p>
                          <div className="text-uppercase font-family-secondary w-100">
                            <NavLink to="/main/payroll/employees/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.employees}</NavLink>
                            <NavLink to="/main/payroll/runs/list" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.payrollRuns}</NavLink>
                          </div>
                        </div>
                        <div className="text-center px-5">
                          <img src={imgPayroll} className="mw-100 mh-200px" alt={lang.labels.payroll} />
                        </div>
                      </div>
                    }
                    { payrollAccess.mode === 'disabled' &&
                      <div id="nav-tab-content-payroll" className="tab-pane fade" role="tabpanel">
                        <div className="text-center pt-5 mb-10">
                          <h2 className="text-uppercase font-family-secondary mb-5">{lang.labels.payroll}</h2>
                          <p className="text-muted fw-bold mb-10">
                            {payrollAccess.availableAsAddon
                              ? lang.labels.navFeatureAvailableAsAddon
                              : lang.labels.navFeatureRequiresUpgrade}
                          </p>
                          <button
                            type="button"
                            className="btn btn-light-primary"
                            onClick={() => payrollAccess.availableAsAddon ? navigate('/main/admin/subscription/addons') : openUpgradeModal('payroll')}
                          >
                            {payrollAccess.availableAsAddon ? lang.labels.addonManageAddons : lang.labels.subscriptionUpgradePlan}
                          </button>
                        </div>
                      </div>
                    }
                    <div id="nav-tab-content-admin" className="tab-pane fade" role="tabpanel">
                      <div className="text-center pt-5 mb-10">
                        <h2 className="text-uppercase font-family-secondary mb-5">{lang.labels.administration}</h2>
                        <p className="text-muted fw-bold mb-10">
                          {lang.labels.selectOneOption}
                          <br/>
                          {lang.labels.youWantToRun}
                        </p>
                        <div className="text-uppercase font-family-secondary w-100">
                          <NavLink to="/main/company/update" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.settings}</NavLink>
                          {showSubscriptionMenu && <NavLink to="/main/admin/subscription" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.subscription}</NavLink>}
                          <NavLink to="/main/admin/users" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.users}</NavLink>
                          <NavLink to="/main/admin/invitations" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.invitations}</NavLink>
                          <NavLink to="/main/admin/roles" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.roles}</NavLink>
                          <NavLink to="/main/admin/policies" className={({isActive}) => `btn btn-primary w-100 my-1 ${isActive && 'active'}`} onClick={executeNavbarAsideHide}>{lang.labels.policies}</NavLink>
                        </div>
                      </div>
                      <div className="text-center px-5">
                        <img src={imgAdministration} className="mw-100 mh-200px" alt={lang.labels.administration} />
                      </div>
                    </div>
                  </>
                }
              </div>
            </div>
            <Footer property={footerNavbar.toString()}>
              <div className="flex-column-auto p-3">
                <Link to="/main/company/create" className="btn btn-bg-dark btn-color-gray-200 btn-active-color-white btn-flex flex-center w-100 py-5 px-3">
                  <span className="btn-label fw-bolder">{lang.labels.createAndSave}</span>
                  <span className="btn-icon svg-icon svg-icon-3 svg-icon-gray-100 ms-2 me-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white">
                      <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22ZM15 17C15 16.4 14.6 16 14 16H8C7.4 16 7 16.4 7 17C7 17.6 7.4 18 8 18H14C14.6 18 15 17.6 15 17ZM17 12C17 11.4 16.6 11 16 11H8C7.4 11 7 11.4 7 12C7 12.6 7.4 13 8 13H16C16.6 13 17 12.6 17 12ZM17 7C17 6.4 16.6 6 16 6H8C7.4 6 7 6.4 7 7C7 7.6 7.4 8 8 8H16C16.6 8 17 7.6 17 7Z" fill="black" />
                      <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="white" />
                    </svg>
                  </span>
                </Link>
              </div>
            </Footer>
          </div>
        </div>
      </div>
      <button id="button-navbar-aside-minimize" className="btn btn-sm btn-icon bg-body btn-color-dark btn-active-secondary position-absolute translate-middle start-100 end-0 bottom-0 border shadow d-none d-lg-flex mb-13" type="button" onClick={executeNavbarAsideMinimize}>
        <span className="svg-icon svg-icon-2 rotate-180">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect opacity="0.5" x="6" y="11" width="13" height="2" rx="1" fill="black" />
            <path d="M8.56569 11.4343L12.75 7.25C13.1642 6.83579 13.1642 6.16421 12.75 5.75C12.3358 5.33579 11.6642 5.33579 11.25 5.75L5.70711 11.2929C5.31658 11.6834 5.31658 12.3166 5.70711 12.7071L11.25 18.25C11.6642 18.6642 12.3358 18.6642 12.75 18.25C13.1642 17.8358 13.1642 17.1642 12.75 16.75L8.56569 12.5657C8.25327 12.2533 8.25327 11.7467 8.56569 11.4343Z" fill="black" />
          </svg>
        </span>
      </button>
      <div id="overlay-menu-transparent" onClick={executeOverlayTransparent}></div>
    </div>
  )
}

export default AppNavbar;